import {
  ArrowLeft,
  Check,
  ChevronRight,
  RotateCcw,
  Sparkles,
  X
} from "lucide-react";
import { useMemo, useState } from "react";
import { BirdPhoto } from "../components/BirdPhoto";
import { birdLabel, birds, type Bird } from "../data/birds";
import { shuffle } from "../utils/random";

type QuizViewProps = {
  questions: Bird[];
  onBack: () => void;
  onAnswer: (id: string, correct: boolean) => void;
};

const optionLetters = ["A", "B", "C", "D"];

export const QuizView = ({
  questions,
  onBack,
  onAnswer
}: QuizViewProps) => {
  const [index, setIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string>();
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const bird = questions[index];

  const options = useMemo(() => {
    if (!bird) return [];
    const alternatives = shuffle(
      birds.filter((candidate) => candidate.id !== bird.id)
    ).slice(0, 3);
    return shuffle([bird, ...alternatives]);
  }, [bird, index]);

  const selectAnswer = (option: Bird) => {
    if (selectedId || !bird) return;
    const correct = option.id === bird.id;
    setSelectedId(option.id);
    if (correct) setScore((current) => current + 1);
    onAnswer(bird.id, correct);
  };

  const next = () => {
    if (index === questions.length - 1) {
      setFinished(true);
      return;
    }
    setSelectedId(undefined);
    setIndex((current) => current + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restart = () => {
    setIndex(0);
    setScore(0);
    setSelectedId(undefined);
    setFinished(false);
  };

  if (finished) {
    const percent = Math.round((score / questions.length) * 100);
    return (
      <main className="focus-page completion-page quiz-completion">
        <div className="completion-card">
          <span className="completion-card__burst" aria-hidden="true">
            <Sparkles size={34} />
          </span>
          <span className="eyebrow">快速测验完成</span>
          <div className="quiz-score">
            <strong>{score}</strong>
            <span>/ {questions.length}</span>
          </div>
          <h1>
            {percent >= 90
              ? "已经很稳了"
              : percent >= 60
                ? "轮廓有了，再加固细节"
                : "错题已经收好，下一轮会更准"}
          </h1>
          <p>答错的鸟已加入复习清单，回到学习模式可以查看完整辨识依据。</p>
          <div className="completion-actions">
            <button className="button button--ghost" onClick={onBack}>
              回到首页
            </button>
            <button className="button button--primary" onClick={restart}>
              <RotateCcw size={18} />
              再测一轮
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!bird) return null;
  const answered = Boolean(selectedId);
  const isCorrect = selectedId === bird.id;

  return (
    <main className="focus-page quiz-page">
      <header className="focus-header">
        <button className="icon-button" onClick={onBack} aria-label="退出测验">
          <ArrowLeft size={21} />
        </button>
        <div className="focus-progress">
          <span>
            看图识鸟
            <small>
              {index + 1} / {questions.length}
            </small>
          </span>
          <i>
            <b style={{ width: `${((index + 1) / questions.length) * 100}%` }} />
          </i>
        </div>
        <span className="quiz-live-score">当前 {score} 分</span>
      </header>

      <section className="quiz-layout">
        <div className="quiz-visual">
          <BirdPhoto images={bird.images} alt="待识别鸟类照片" eager />
          <div className="quiz-visual__prompt">
            <small>观察照片</small>
            <h1>这只鸟是？</h1>
          </div>
        </div>
        <div className="quiz-options">
          <span className="section-kicker">选择最准确的答案</span>
          <div className="quiz-options__list">
            {options.map((option, optionIndex) => {
              const correctOption = answered && option.id === bird.id;
              const wrongOption =
                answered && option.id === selectedId && option.id !== bird.id;
              return (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => selectAnswer(option)}
                  className={`${correctOption ? "is-correct" : ""} ${
                    wrongOption ? "is-wrong" : ""
                  }`}
                  disabled={answered}
                >
                  <b>{optionLetters[optionIndex]}</b>
                  <span>{birdLabel(option)}</span>
                  {correctOption && <Check size={20} />}
                  {wrongOption && <X size={20} />}
                </button>
              );
            })}
          </div>

          {answered && (
            <div className={`quiz-feedback ${isCorrect ? "is-correct" : "is-wrong"}`}>
              <div>
                {isCorrect ? <Check size={20} /> : <X size={20} />}
                <span>
                  <strong>{isCorrect ? "判断正确" : `正确答案：${birdLabel(bird)}`}</strong>
                  <small>{bird.memory}</small>
                </span>
              </div>
              {!isCorrect && (
                <p>
                  <b>别和{bird.similar.name}混：</b>
                  {bird.similar.difference}
                </p>
              )}
              <button className="button button--ink" onClick={next}>
                {index === questions.length - 1 ? "查看结果" : "下一题"}
                <ChevronRight size={17} />
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};
